// The wire, as the page reads it: the edge's tagged headlines, plus every headline this browser has
// seen in the last day and a half (outlets drop older items from their feeds).

import type { Wire, WireItem } from "../../shared/newsroom/types.ts";

// Headlines leave a feed when the outlet posts newer ones, not when they stop mattering: the page
// keeps every headline it has seen for as long as a move can still be tied to one, across reloads.
const KEEP_MS = 36 * 3_600_000;
const KEEP_MAX = 1_500;
const SAVED = "newspaper.hl:wire:1";

const isItem = (value: unknown): value is WireItem => {
  const item = value as WireItem;
  return typeof item?.id === "string" && typeof item.title === "string" && typeof item.ts === "number" && typeof item.tags === "object" && item.tags !== null;
};

function readSaved(): WireItem[] {
  try {
    const raw = localStorage.getItem(SAVED);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isItem) : [];
  } catch {
    return [];
  }
}

const seen = new Map<string, WireItem>(readSaved().map((item) => [item.id, item]));

function remember(items: WireItem[], now: number): WireItem[] {
  for (const item of items) seen.set(item.id, item);
  const kept = [...seen.values()]
    .filter((item) => now - item.ts <= KEEP_MS)
    .sort((a, b) => b.ts - a.ts)
    .slice(0, KEEP_MAX);
  seen.clear();
  for (const item of kept) seen.set(item.id, item);
  try {
    localStorage.setItem(SAVED, JSON.stringify(kept));
  } catch {
    // a full or blocked store only costs the next reload what rotated out
  }
  return kept;
}

export async function loadWire(): Promise<Wire> {
  const response = await fetch("/feed/wire", { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`the wire answered ${response.status}`);
  const wire = (await response.json()) as Wire;
  return { ...wire, items: remember(wire.items, Date.now()) };
}
